import type { Directive } from "vue";

import { ModalHelper } from "@/helpers";

type InputElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

interface Config {
  ignoreTargetSelectors: string[];
  ignoreKeys: string[];
  active: boolean;
}

const defaultConfig: Config = {
  ignoreTargetSelectors: ["input", "textarea", "select", "[role=listbox]"],
  ignoreKeys: [],
  active: true
};

const configRegistry = new WeakMap<InputElement, Config>();

function createConfig(config?: Partial<Config>): Config {
  return { ...defaultConfig, ...config };
}

function setConfig(el: InputElement, config: Config): void {
  configRegistry.set(el, config);
}

function getConfig(el: InputElement): Config | undefined {
  return configRegistry.get(el);
}

/**
 * Checks if target should be ignored.
 */
function isIgnoredTarget(targetEl: InputElement, config: Config) {
  return config.ignoreTargetSelectors.some((tag) => targetEl.matches?.(tag));
}

/**
 * Checks if key input should be ignored.
 */
function isIgnoredKeyEvent(event: KeyboardEvent, config: Config) {
  return config.ignoreKeys.map((key) => key.toLowerCase()).includes(event.key.toLowerCase());
}

/**
 * Checks if event will produce a printable character.
 */
function isPrintableKeyEvent(event: KeyboardEvent) {
  return [...event.key].length === 1 && !event.ctrlKey && !event.metaKey;
}

/**
 * Tries to give focus to element.
 */
function setFocus(el: InputElement) {
  el.focus();
  return document.activeElement === el;
}

/**
 * Set element value and notifies event listeners.
 */
function setValue(el: InputElement, value: string) {
  el.value = value;
  el.dispatchEvent(
    new Event("input", {
      bubbles: true
    })
  );
}

/**
 * Keep track of listeners to be able to clean up later.
 */
const listenerRegistry = new WeakMap<InputElement, (event: Event) => void>();

function addListeners(el: InputElement): void {
  const listener = (event: Event): void => {
    const config = getConfig(el);
    if (!config || !config.active) {
      return;
    }

    if (isIgnoredTarget(event.target as InputElement, config)) {
      return;
    }

    if (event instanceof KeyboardEvent) {
      if (isIgnoredKeyEvent(event, config)) {
        return;
      }
      if (!isPrintableKeyEvent(event)) {
        return;
      }

      if (ModalHelper.isAnyModalOpen()) {
        return;
      }

      if (setFocus(el)) {
        setValue(el, event.key);
        event.preventDefault();
      }
    }

    if (event instanceof ClipboardEvent) {
      if (!event.clipboardData) {
        return;
      }

      if (setFocus(el)) {
        setValue(el, event.clipboardData.getData("text").trim());
        event.preventDefault();
      }
    }
  };

  listenerRegistry.set(el, listener);
  ["keydown", "paste"].forEach((eventType) => window.addEventListener(eventType, listener));
}

function removeListeners(el: InputElement): void {
  const listener = listenerRegistry.get(el);
  if (listener) {
    ["keydown", "paste"].forEach((eventType) => window.removeEventListener(eventType, listener));
  }
}

const vFocusOnType: Directive<InputElement, Partial<Config>> = {
  mounted(el, { value }) {
    setConfig(el, createConfig(value));
    addListeners(el);
  },
  updated(el, { value }) {
    setConfig(el, createConfig(value));
  },
  beforeUnmount(el) {
    removeListeners(el);
  }
};

export { createConfig, vFocusOnType };
